export const CONSTANTS = {
  RECORDS_PER_PAGE: 14,
  FRONT_CARD_RECORDS: 14,
  BACK_CARD_RECORDS: 24,
  RECORDS_PER_BOOK_CARD: 38, // 14 front + 24 back
  FALLBACK_VALUES: {
    BARCODE: "N/A",
    CALL_NUMBER: "N/A",
  },
};

/**
 * Extracts a string ID from various identifier formats
 * @param {*} identifier - The identifier to extract from
 * @returns {string|null} The extracted string ID or null
 */
export function extractStringId(identifier) {
  if (!identifier) return null;
  if (typeof identifier === "string") return identifier;
  if (typeof identifier === "number") return String(identifier);
  if (typeof identifier === "object") {
    return (
      identifier.value || identifier.id || identifier.pid || String(identifier)
    );
  }
  return String(identifier);
}

/**
 * Normalizes item data to ensure consistent object structure
 * @param {*} data - The data to normalize
 * @returns {Object} Normalized item data object
 */
export function normalizeItemData(data) {
  if (!data) return {};
  if (Array.isArray(data)) {
    return (
      data.find(
        (item) => item && typeof item === "object" && !Array.isArray(item)
      ) || {}
    );
  }
  return typeof data === "object" ? data : {};
}

/**
 * Extracts call number suffix from item data
 * @param {Object} itemData - The item data object
 * @returns {string} The call number suffix or empty string
 */
export function extractCallNumberSuffix(itemData) {
  if (!itemData) return "";
  return itemData.call_number_suffix || "";
}

/**
 * Extracts suffix number for sorting purposes
 * @param {string} callNumberSuffix - The call number suffix
 * @returns {number} The numeric part of the suffix or 999 as fallback
 */
export function extractSuffixNumber(callNumberSuffix) {
  if (!callNumberSuffix) return 999;
  const match = callNumberSuffix.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 999;
}

/**
 * Filters loan history for a specific item
 * @param {Array} loanHistory - The complete loan history
 * @param {string} selectedItemId - The ID of the selected item
 * @returns {Array} Filtered loan history for the selected item
 */
export function filterLoanHistoryByItem(loanHistory, selectedItemId) {
  if (!selectedItemId) return loanHistory;

  return loanHistory.filter((loan) => {
    const loanItemId =
      loan.item_pid ||
      loan.item?.pid ||
      loan.metadata?.item_pid ||
      loan.metadata?.item?.pid;
    const actualItemId = extractStringId(loanItemId);
    return actualItemId === selectedItemId;
  });
}

/**
 * Extracts available items from document and loan history
 * @param {Object} document - The document object
 * @param {Array} loanHistory - The loan history array
 * @returns {Array} Array of available items with normalized data
 */
export function extractAvailableItems(document, loanHistory) {
  const itemsMap = new Map();
  const seenBarcodes = new Set();

  // Process document items first
  const documentItems = document?.metadata?.items || [];
  let itemsToProcess = [];

  if (documentItems.hits && Array.isArray(documentItems.hits)) {
    itemsToProcess = documentItems.hits;
  } else if (Array.isArray(documentItems)) {
    if (
      documentItems.length > 0 &&
      documentItems[0] &&
      typeof documentItems[0] === "object" &&
      "barcode" in documentItems[0]
    ) {
      itemsToProcess = documentItems;
    }
  }

  itemsToProcess.forEach((item, index) => {
    const rawId = item.pid || item.id || item.barcode || `doc-item-${index}`;
    const itemId = extractStringId(rawId);
    const itemBarcode = extractStringId(item.barcode);
    const callNumberSuffix = extractCallNumberSuffix(item);

    if (
      itemId &&
      itemBarcode &&
      itemBarcode !== CONSTANTS.FALLBACK_VALUES.BARCODE &&
      !seenBarcodes.has(itemBarcode)
    ) {
      seenBarcodes.add(itemBarcode);
      itemsMap.set(itemId, {
        id: itemId,
        barcode: itemBarcode,
        callNumber: item.call_number || item.callNumber,
        callNumberSuffix: callNumberSuffix,
        source: "document",
        itemData: {
          ...normalizeItemData(item),
          call_number_suffix: callNumberSuffix,
        },
      });
    }
  });

  // Process loan history items
  loanHistory.forEach((loan) => {
    const rawId = loan.item_pid || loan.item?.pid || loan.metadata?.item_pid;
    const itemId = extractStringId(rawId);
    const itemBarcode = extractStringId(
      loan.item?.barcode || loan.metadata?.item?.barcode
    );
    const itemCallNumber =
      loan.item?.call_number || loan.metadata?.item?.call_number;
    const callNumberSuffix = extractCallNumberSuffix(loan.item || {});

    if (
      itemId &&
      itemBarcode &&
      itemBarcode !== CONSTANTS.FALLBACK_VALUES.BARCODE &&
      !seenBarcodes.has(itemBarcode) &&
      !itemsMap.has(itemId)
    ) {
      seenBarcodes.add(itemBarcode);
      itemsMap.set(itemId, {
        id: itemId,
        barcode: itemBarcode,
        callNumber: itemCallNumber,
        callNumberSuffix: callNumberSuffix,
        source: "loanHistory",
        itemData: {
          ...normalizeItemData(loan.item),
          call_number_suffix: callNumberSuffix,
        },
      });
    }
  });

  // Fallback default item if no items found
  if (itemsMap.size === 0) {
    const documentCallNumber =
      document?.metadata?.call_number || document?.metadata?.callNumber;

    const defaultItem = {
      id: "default",
      barcode: CONSTANTS.FALLBACK_VALUES.BARCODE,
      callNumber: documentCallNumber || CONSTANTS.FALLBACK_VALUES.CALL_NUMBER,
      callNumberSuffix: "",
      source: "default",
      itemData: {},
    };

    itemsMap.set("default", defaultItem);
  }

  // Sort by copy number (extracted from call number suffix), then by barcode
  const finalItems = Array.from(itemsMap.values()).sort((a, b) => {
    const suffixA = extractSuffixNumber(a.callNumberSuffix);
    const suffixB = extractSuffixNumber(b.callNumberSuffix);

    // First sort by suffix number
    if (suffixA !== suffixB) {
      return suffixA - suffixB;
    }

    // If suffix numbers are the same, sort by barcode as fallback
    return (a.barcode || a.id).localeCompare(b.barcode || b.id);
  });

  return finalItems;
}

/**
 * Calculates the total number of book cards needed for a given loan history
 * @param {Array} filteredLoanHistory - Filtered loan history for an item
 * @returns {number} Total number of book cards needed
 */
export function calculateTotalBookCards(filteredLoanHistory) {
  return (
    Math.ceil(filteredLoanHistory.length / CONSTANTS.RECORDS_PER_BOOK_CARD) || 1
  );
}

/**
 * Gets loan records for a specific book card (front side)
 * @param {Array} filteredLoanHistory - Filtered loan history for an item
 * @param {number} currentBookCard - Current book card index
 * @returns {Array} Loan records for the front of the specified book card
 */
export function getFrontCardLoanRecords(filteredLoanHistory, currentBookCard) {
  const startIndex = currentBookCard * CONSTANTS.RECORDS_PER_BOOK_CARD;
  const endIndex = startIndex + CONSTANTS.FRONT_CARD_RECORDS;
  return filteredLoanHistory.slice(startIndex, endIndex);
}

/**
 * Gets loan records for a specific book card (back side)
 * @param {Array} filteredLoanHistory - Filtered loan history for an item
 * @param {number} currentBookCard - Current book card index
 * @returns {Array} Loan records for the back of the specified book card
 */
export function getBackCardLoanRecords(filteredLoanHistory, currentBookCard) {
  const startIndex =
    currentBookCard * CONSTANTS.RECORDS_PER_BOOK_CARD +
    CONSTANTS.FRONT_CARD_RECORDS;
  const endIndex = (currentBookCard + 1) * CONSTANTS.RECORDS_PER_BOOK_CARD;
  return filteredLoanHistory.slice(startIndex, endIndex);
}

/**
 * Transforms loan data into standardized loan records for display
 * @param {Array} loans - Raw loan data array
 * @returns {Array} Standardized loan records
 */
export function transformLoansToRecords(loans) {
  return loans.map((loan) => ({
    checkOutDate:
      loan.metadata?.start_date || loan.start_date || loan.checkout_date || "",
    borrowerName:
      loan.metadata?.patron?.name ||
      loan.patron?.full_name ||
      `${loan.metadata?.patron?.first_name || loan.patron?.first_name || ""} ${
        loan.metadata?.patron?.last_name || loan.patron?.last_name || ""
      }`.trim() ||
      "",
    returnDate:
      loan.metadata?.end_date || loan.end_date || loan.return_date || "",
    dueDate: loan.metadata?.due_date || loan.due_date || "",
  }));
}
